import {
  MetaobjectEdge,
  Product,
  ProductVariant,
} from '@shopify/hydrogen-react/storefront-api-types';
import {
  META_OBJECT,
  PRODUCT_DETAILS_QUERY,
  PRODUCTS_BY_IDS,
  VARIANTS_QUERY,
  PRODUCT_VARIANTS_PAGINATED_QUERY,
} from './queries';
import { removeExclusiveProducts, userCanLoadExclusiveItems } from '../exclusiveProducts';
import { executeGraphQLQuery } from '../storefrontClient';
import { prepareIds } from '../utils';

// Helper function to fetch all variants using pagination
const fetchAllVariants = async (handle: string): Promise<ProductVariant[]> => {
  let allVariants: ProductVariant[] = [];
  let hasNextPage = true;
  let cursor: string | null = null;
  const first = 250; // Maximum allowed per request
  let pageCount = 0;

  while (hasNextPage) {
    try {
      pageCount++;

      const response = await executeGraphQLQuery(PRODUCT_VARIANTS_PAGINATED_QUERY, {
        handle,
        first,
        after: cursor,
      });

      if (response.data?.product?.variants) {
        const { edges, pageInfo } = response.data.product.variants;

        const newVariants = edges.map((edge: any) => edge.node);
        allVariants = [...allVariants, ...newVariants];

        hasNextPage = pageInfo.hasNextPage;
        cursor = pageInfo.endCursor;
      } else {
        hasNextPage = false;
      }
    } catch (error) {
      hasNextPage = false;
    }
  }
  return allVariants;
};

export const getProductDetails = async (handle: string = ''): Promise<Product | undefined> => {
  try {
    const productDetails = await executeGraphQLQuery(PRODUCT_DETAILS_QUERY, { handle });

    // Check if we need to fetch additional variants via pagination
    const currentVariants = productDetails.data.product.variants?.edges || [];

    // If we have exactly 250 variants, there might be more, so let's check with pagination
    if (currentVariants.length >= 250) {
      const allVariants = await fetchAllVariants(handle);

      // Replace the variants in the product data
      productDetails.data.product.variants = {
        edges: allVariants.map((variant: ProductVariant) => ({
          cursor: variant.id, // Using id as cursor for compatibility
          node: variant,
        })),
      };
    }

    return Promise.resolve(productDetails.data.product);
  } catch (error) {
    return Promise.reject(error);
  }
};

export const getVariants = async (ids: string[] = []): Promise<ProductVariant[] | undefined> => {
  try {
    const variants = await executeGraphQLQuery(VARIANTS_QUERY, { ids });
    return Promise.resolve(variants.data.nodes);
  } catch (error) {
    return Promise.reject(error);
  }
};

export const getProductVariantsPaginated = async (
  handle: string = ''
): Promise<ProductVariant[] | undefined> => {
  try {
    const allVariants = await fetchAllVariants(handle);
    return Promise.resolve(allVariants);
  } catch (error) {
    return Promise.reject(error);
  }
};

// Utility function to check if a product needs pagination
export const needsVariantPagination = (product: Product): boolean => {
  const currentVariants = product.variants?.edges || [];
  return currentVariants.length >= 250;
};

// Function to force fetch all variants for a product (useful for products known to have many variants)
export const getProductDetailsWithAllVariants = async (
  handle: string = ''
): Promise<Product | undefined> => {
  try {
    const productDetails = await executeGraphQLQuery(PRODUCT_DETAILS_QUERY, { handle });

    // Always fetch all variants via pagination
    const allVariants = await fetchAllVariants(handle);

    // Replace the variants in the product data
    productDetails.data.product.variants = {
      edges: allVariants.map((variant: ProductVariant) => ({
        cursor: variant.id,
        node: variant,
      })),
    };

    return Promise.resolve(productDetails.data.product);
  } catch (error) {
    return Promise.reject(error);
  }
};

export const getMetaobjectValues = async (
  type: string = ''
): Promise<MetaobjectEdge[] | undefined> => {
  try {
    const productsMetadata = await executeGraphQLQuery(META_OBJECT, { type });
    return Promise.resolve(productsMetadata.data.metaobjects.edges);
  } catch (error) {
    return Promise.reject(error);
  }
};

export const getProductsByIds = async (ids: string[]): Promise<Product[] | undefined> => {
  try {
    let preparedIds = prepareIds(ids);
    const productsMetadata = await executeGraphQLQuery(PRODUCTS_BY_IDS, { ids: preparedIds });

    if (productsMetadata.data.nodes && !userCanLoadExclusiveItems()) {
      productsMetadata.data.nodes = removeExclusiveProducts(productsMetadata.data.nodes);
    }

    return Promise.resolve(productsMetadata.data.nodes);
  } catch (error) {
    return Promise.reject(error);
  }
};
